<template>
  <div class="rn-slider-area" id="home" v-if="NOTARIA && NOTARIA.Theme">
    <div class="banner-modern-wrapper" :style="wrapperStyle">
      <v-container>
        <v-row :class="['banner-row-modern', { 'flex-row-reverse': NOTARIA.Theme.Home.Banner.ImageLeft }]">
          <!-- Columna 1: Información -->
          <v-col lg="7" md="7" sm="12" cols="12">
            <div class="banner-content-modern banner-glass-premium" :style="{ textAlign: style.ThemeHomeBanner?.Titulo?.css?.Align || '' }">
              <div class="logo-wrapper-modern mb--30" v-if="NOTARIA.Theme.Home.Banner.Logo && !ModoAccesible" :style="logoBlockStyle">
                <img class="img-fluid logo-premium" :style="logoImgStyle" 
                    :src="NOTARIA?.Theme?.Home?.Banner?.ImagenLogo?.trim() ? imagenIDtoSRC(NOTARIA.Theme.Home.Banner.ImagenLogo) : '/img/logo-notariado.png'"
                    :alt="'Logo de la ' + NOTARIA.Nombre"
                />
              </div>

              <h1 class="heading-modern mb--20" :style="tituloStyle" v-html="NOTARIA.Theme.Home.Banner.Titulo.Texto"></h1>
              
              <div class="sub-wrapper mb--30">
                <h2 class="subheading-modern" :style="subtituloStyle" v-html="NOTARIA.Theme.Home.Banner.Subtitulo.Texto"></h2>
              </div>

              <div v-if="NOTARIA.Theme.Home.Banner.Texto" class="description-modern mb--40"
                  :style="textoStyle" v-html="NOTARIA.Theme.Home.Banner.Texto.Texto"></div>

              <div class="slide-btn-modern d-flex flex-wrap gap-20" :style="buttonsBlockStyle">
                <v-btn id="button-menu" 
                       v-if="NOTARIA.Theme.Home.Banner.ShowButtonMoveMenu"  
                       class="rn-btn btn-modern-primary" 
                       :style="buttonMenuStyle"  
                       @click="goTo(compMenuAction)">
                  {{ NOTARIA.Theme.Home.Banner.ButtonMoveMenu.Texto }}
                </v-btn>

                <v-btn id="button-inicio" 
                       v-if="NOTARIA.Theme.Home.Banner.ShowButtonMoveInicio" 
                       class="rn-btn btn-modern-secondary" 
                       :style="buttonInicioStyle"
                       @click="scrollMeTo(compInicioAction)">
                  {{ NOTARIA.Theme.Home.Banner.ButtonMoveInicio.Texto }}
                </v-btn>
              </div>
            </div>
          </v-col>

          <!-- Columna 2: Imagen/Video -->
          <v-col lg="5" md="5" sm="12" cols="12">
            <div class="banner-image-container-modern" :style="imageContainerStyle">
              <video v-if="file && file.type === 'video/mp4'" class="banner-video-modern" :src="imagenIDtoSRC(style.ThemeHomeBanner.Imagen)"
                     preload="metadata" muted autoplay playsinline loop />
              <img v-else :src="imagenIDtoSRC(style.ThemeHomeBanner.Imagen)" class="banner-img-modern" alt="Banner Notaría" />
              <div class="image-overlay-modern"></div>
            </div>
          </v-col>
        </v-row>
      </v-container>

      <div v-if="NOTARIA.Theme.Home.Banner.ShowArrow" class="scroll-indicator-modern" @click="handleArrowClick">
        <v-icon :color="NOTARIA?.Theme?.General?.TituloCss?.Color || 'primary'">mdi-chevron-down</v-icon>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useStore } from '../../store'
import { useUtils } from '#imports'
import { navigateTo } from '#app/composables/router'
import { TiposAccionBoton } from '~/helpers/Enums/Enum'
import { useStyles } from '../../store/styles'

const store = useStore()
const { NOTARIA, ModoAccesible } = storeToRefs(store)
const style = useStyles()
const { imagenIDtoSRC, getFile } = useUtils()

const file = computed(() => getFile(style.ThemeHomeBanner?.Imagen))
const imgFiltro = computed(() => style.ThemeHomeBanner?.ImagenFiltro ?? '')

const compMenuAction = computed(() =>
  NOTARIA.value?.Theme?.Home?.Banner?.ButtonMoveMenu ? getTipoAccion(NOTARIA.value.Theme.Home.Banner.ButtonMoveMenu.Click) : null
)

const compInicioAction = computed(() =>
  NOTARIA.value?.Theme?.Home?.Banner?.ButtonMoveInicio ? getTipoAccion(NOTARIA.value.Theme.Home.Banner.ButtonMoveInicio.Click) : null
)

const wrapperStyle = computed(() => ({
  backgroundColor: NOTARIA.value?.Theme?.General?.FondoCss?.BackgroundColor || 'transparent',
  minHeight: '90vh',
  display: 'flex',
  alignItems: 'center',
  position: 'relative',
  padding: '100px 0'
}))

const logoBlockStyle = computed(() => {
    const align = style.ThemeHomeBanner?.ImagenLogoBlock?.css?.Align || '';
    return {
        display: 'flex',
        justifyContent: align === 'center' ? 'center' : (align === 'right' ? 'flex-end' : 'flex-start')
    }
})

const logoImgStyle = computed(() => ({
  maxWidth: style.ThemeHomeBanner?.ImagenLogoBlock?.css?.Size ?? '180px',
  height: 'auto'
}))

const tituloStyle = computed(() => ({
  color: style.ThemeHomeBanner?.Titulo?.css?.Color || '',
  fontFamily: style.ThemeHomeBanner?.Titulo?.css?.Font || '',
  fontSize: style.ThemeHomeBanner?.Titulo?.css?.Size || '',
  fontWeight: style.ThemeHomeBanner?.Titulo?.css?.Weight || '',
  textAlign: style.ThemeHomeBanner?.Titulo?.css?.Align || '',
  textShadow: style.ThemeHomeBanner?.Titulo?.css?.BackgroundColor ? `4px 2px 6px ${style.ThemeHomeBanner.Titulo.css.BackgroundColor}` : ''
}))

const subtituloStyle = computed(() => ({
  color: style.ThemeHomeBanner?.Subtitulo?.css?.Color || '',
  fontFamily: style.ThemeHomeBanner?.Subtitulo?.css?.Font || '',
  fontSize: style.ThemeHomeBanner?.Subtitulo?.css?.Size || '',
  fontWeight: style.ThemeHomeBanner?.Subtitulo?.css?.Weight || '',
  textAlign: style.ThemeHomeBanner?.Subtitulo?.css?.Align || '',
  textShadow: style.ThemeHomeBanner?.Subtitulo?.css?.BackgroundColor ? `4px 2px 6px ${style.ThemeHomeBanner.Subtitulo.css.BackgroundColor}` : ''
}))

const textoStyle = computed(() => ({
  color: style.ThemeHomeBanner?.Texto?.css?.Color || '',
  fontFamily: style.ThemeHomeBanner?.Texto?.css?.Font || '',
  fontSize: style.ThemeHomeBanner?.Texto?.css?.Size || '',
  fontWeight: style.ThemeHomeBanner?.Texto?.css?.Weight || '',
  textAlign: style.ThemeHomeBanner?.Texto?.css?.Align || '',
  textShadow: style.ThemeHomeBanner?.Texto?.css?.BackgroundColor ? `4px 2px 6px ${style.ThemeHomeBanner.Texto.css.BackgroundColor}` : ''
}))

const buttonsBlockStyle = computed(() => {
    const align = style.ThemeHomeBanner?.ButtonsBlock?.css?.Align || '';
    return {
        justifyContent: align === 'center' ? 'center' : (align === 'right' ? 'flex-end' : 'flex-start')
    }
})

const buttonMenuStyle = computed(() => ({
  color: style.ThemeHomeBanner?.ButtonMoveMenu?.css?.Color || '',
  fontFamily: style.ThemeHomeBanner?.ButtonMoveMenu?.css?.Font || '',
  backgroundColor: style.ThemeHomeBanner?.ButtonMoveMenu?.css?.BackgroundColor || '',
  borderColor: style.ThemeHomeBanner?.ButtonMoveMenu?.css?.BackgroundColor || '',
  fontSize: style.ThemeHomeBanner?.ButtonMoveMenu?.css?.Size || '',
  fontWeight: style.ThemeHomeBanner?.ButtonMoveMenu?.css?.Weight || '',
  border: style.ThemeHomeBanner?.ButtonMoveMenu?.css?.Align || ''
}))

const buttonInicioStyle = computed(() => ({
  color: style.ThemeHomeBanner?.ButtonMoveInicio?.css?.Color || '',
  fontFamily: style.ThemeHomeBanner?.ButtonMoveInicio?.css?.Font || '',
  backgroundColor: style.ThemeHomeBanner?.ButtonMoveInicio?.css?.BackgroundColor || '',
  fontSize: style.ThemeHomeBanner?.ButtonMoveInicio?.css?.Size || '',
  fontWeight: style.ThemeHomeBanner?.ButtonMoveInicio?.css?.Weight || '',
  border: style.ThemeHomeBanner?.ButtonMoveInicio?.css?.Align || ''
}))

const imageContainerStyle = computed(() => ({
  filter: imgFiltro.value,
  WebkitFilter: imgFiltro.value
}))

function goTo(refName: TiposAccionBoton) {
  if (!refName) return
  if (refName === TiposAccionBoton.NV) {
    navigateTo(NOTARIA.value.NotariaVirtual, { external: true })
  } else {
    navigateTo(refName.resumen)
  }
}

function scrollMeTo(refName: TiposAccionBoton) {
  if (!refName || refName === TiposAccionBoton.undefined) return
  const element = document.getElementById(refName.name) || document.querySelector(refName.name)
  if (element) window.scrollTo({ top: element.offsetTop - 80, behavior: 'smooth' })
}

function scrollToNextSection() {
  const nextSection = document.querySelector('.home-section');
  if (nextSection) {
    window.scrollTo({
      top: nextSection.offsetTop - 80,
      behavior: 'smooth'
    });
  }
}

function handleArrowClick() {
  const actionName = NOTARIA.value?.Theme?.Home?.Banner?.ArrowAction;
  if (actionName) {
    const action = getTipoAccion(actionName);
    scrollMeTo(action);
  } else {
    scrollToNextSection();
  }
}

function getTipoAccion(value: string) {
  switch (value) {
    case 'about': return TiposAccionBoton.Notario
    case 'service': return TiposAccionBoton.Servicios
    case 'blog': return TiposAccionBoton.Blog
    case 'testimonial': return TiposAccionBoton.Testimonial
    case 'homegallery': return TiposAccionBoton.Gallery
    case 'Inicio': return TiposAccionBoton.Inicio
    case 'contact': return TiposAccionBoton.Contacto
    case 'NV': return TiposAccionBoton.NV
    case 'equipo': return TiposAccionBoton.Equipo
    default: return TiposAccionBoton.undefined
  }
}
</script>

<style scoped lang="scss">
.banner-modern-wrapper {
  overflow: hidden;
}

.banner-row-modern {
  align-items: stretch;
}

.banner-content-modern {
  animation: fadeInLeft 1s ease-out;
}

.logo-premium {
  filter: drop-shadow(0 5px 15px rgba(0,0,0,0.1));
}

.banner-glass-premium {
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(0, 0, 0, 0.05);
  border-radius: 24px;
  padding: 40px;
  box-shadow: 0 8px 32px 0 rgba(0, 0, 0, 0.05);
  transition: all 0.4s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;

  &:hover {
      box-shadow: 0 8px 32px 0 rgba(0, 0, 0, 0.1);
      border-color: rgba(0, 0, 0, 0.1);
  }
}

.heading-modern {
  letter-spacing: -1px;
}

.description-modern {
  width: 100%;
}

.gap-20 {
  gap: 20px;
}

.btn-modern-primary, .btn-modern-secondary {
  text-transform: none !important;
  font-weight: 700;
  letter-spacing: 1px;
  border-radius: 12px;
  padding: 0 35px;
  height: 54px;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);

  &::before {
      content: '';
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
      transition: 0.5s;
  }

  &:hover {
    transform: translateY(-3px) scale(1.01);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
    
    &::before {
        left: 100%;
    }
  }
}

.banner-image-container-modern {
  position: relative;
  border-radius: 24px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0,0,0,0.2);
  height: 100%;
  min-height: 400px;
  animation: fadeInRight 1s ease-out;

  .banner-img-modern, .banner-video-modern {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.6s ease;
  }

  &:hover {
    .banner-img-modern, .banner-video-modern {
      transform: scale(1.05);
    }
  }
}

.image-overlay-modern {
  position: absolute;
  inset: 0;
  background: linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.2) 100%);
}

.scroll-indicator-modern {
  position: absolute;
  bottom: 30px;
  left: 50%;
  transform: translateX(-50%);
  cursor: pointer;
  animation: bounce 2s infinite;
}

@keyframes fadeInLeft {
  from { opacity: 0; transform: translateX(-50px); }
  to { opacity: 1; transform: translateX(0); }
}

@keyframes fadeInRight {
  from { opacity: 0; transform: translateX(50px); }
  to { opacity: 1; transform: translateX(0); }
}

@keyframes bounce {
  0%, 20%, 50%, 80%, 100% {transform: translateX(-50%) translateY(0);}
  40% {transform: translateX(-50%) translateY(-10px);}
  60% {transform: translateX(-50%) translateY(-5px);}
}

@media (max-width: 960px) {
  .banner-content-modern {
    text-align: center !important;
    padding: 40px 0;
  }
  .slide-btn-modern {
    justify-content: center !important;
  }
  .description-modern {
    margin: 0 auto 40px !important;
  }
  .logo-wrapper-modern {
    justify-content: center !important;
  }
}
</style>
